import React, { useState } from 'react';
import { MdOutlineWarningAmber, MdOutlineInfo, MdOutlineExpandMore, MdOutlineExpandLess } from 'react-icons/md';
import { STATUS_LABELS, statusTone, DASH } from '../format';
import Sparkline from '../charts/Sparkline';

export function Pill({ tone = 'neutral', children, title, className = '' }) {
  return <span className={`le-pill le-pill-${tone} ${className}`} title={title}>{children}</span>;
}

export function Chip({ children, title, mono = false }) {
  return <span className={`le-chip ${mono ? 'le-chip-mono' : ''}`} title={title}>{children}</span>;
}

/** Stat tile: label · value · optional sub/delta/sparkline. */
export function StatTile({ label, value, sub, delta, spark, hint, emphasis = false, size = 'md' }) {
  return (
    <div className={`le-stat ${emphasis ? 'le-stat-emphasis' : ''} le-stat-${size}`} title={hint}>
      <div className="le-stat-label">{label}</div>
      <div className="le-stat-value">{value === null || value === undefined || value === '' ? DASH : value}</div>
      {(sub || delta || spark) ? (
        <div className="le-stat-foot">
          {delta ? <span className={`le-delta le-delta-${delta.tone || 'flat'}`}>{delta.label}</span> : null}
          {sub ? <span className="le-stat-sub">{sub}</span> : null}
          {spark && spark.length > 1 ? <Sparkline values={spark} /> : null}
        </div>
      ) : null}
    </div>
  );
}

export function FactGrid({ items = [], columns }) {
  const visible = items.filter((it) => it && !it.hidden);
  if (!visible.length) return null;
  return (
    <dl className="le-facts" style={columns ? { '--facts-columns': columns } : undefined}>
      {visible.map((it) => (
        <div key={it.label} className="le-fact" title={it.hint}>
          <dt>{it.label}</dt>
          <dd className={it.muted ? 'muted' : ''}>{it.value === null || it.value === undefined || it.value === '' ? DASH : it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Note({ tone = 'info', children }) {
  const Icon = tone === 'warning' || tone === 'critical' ? MdOutlineWarningAmber : MdOutlineInfo;
  return (
    <div className={`le-note le-note-${tone}`}>
      <Icon size={16} />
      <div>{children}</div>
    </div>
  );
}

export function Disclosure({ summary, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`le-disclosure ${open ? 'open' : ''}`}>
      <button type="button" className="le-disclosure-summary" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {open ? <MdOutlineExpandLess size={18} /> : <MdOutlineExpandMore size={18} />}
        <span>{summary}</span>
      </button>
      {open ? <div className="le-disclosure-body">{children}</div> : null}
    </div>
  );
}

export function StatusPill({ status }) {
  if (!status) return null;
  const tone = statusTone(status.status);
  const warnings = Array.isArray(status.warnings) ? status.warnings : [];
  const title = warnings.length ? warnings.join('\n') : (status.method || undefined);
  return <Pill tone={tone} title={title}>{STATUS_LABELS[status.status] || status.status}</Pill>;
}

function unavailableCopy(status) {
  switch (status?.status) {
    case 'not_found': return 'The source covers this kind of lookup but has no matching record for this location.';
    case 'not_covered': return 'This location is outside the source\'s geographic or temporal coverage.';
    case 'not_available': return 'This dataset is not configured on the backend you are connected to.';
    case 'error': return 'This dataset failed or timed out. Every other dataset still returned — try again in a moment.';
    case 'not_requested': return 'This dataset was not requested.';
    default: return 'No data returned for this dataset.';
  }
}

/**
 * Card wrapper for one dataset: header (icon, title, status), warnings strip
 * on partial results, and an honest "unavailable" body when the dataset is null.
 */
export function SectionCard({ id, icon, title, subtitle, status, dataset, headerRight, children, className = '' }) {
  const warnings = Array.isArray(status?.warnings) ? status.warnings : [];
  const missing = dataset === null || dataset === undefined;
  return (
    <section id={id} className={`le-card ${className} ${missing ? 'le-card-missing' : ''}`} aria-labelledby={`${id}-title`}>
      <header className="le-card-head">
        <div className="le-card-head-main">
          {icon ? <span className="le-card-icon">{icon}</span> : null}
          <div className="le-card-titles">
            <h3 id={`${id}-title`} className="le-card-title">{title}</h3>
            {subtitle ? <div className="le-card-subtitle">{subtitle}</div> : null}
          </div>
        </div>
        <div className="le-card-head-right">
          {headerRight}
          {status && status.status !== 'ok' ? <StatusPill status={status} /> : null}
        </div>
      </header>
      {missing ? (
        <div className="le-card-body">
          <Note tone={status?.status === 'error' ? 'critical' : 'muted'}>
            <strong>{STATUS_LABELS[status?.status] || 'Unavailable'}.</strong> {unavailableCopy(status)}
            {warnings.length ? (
              <ul className="le-warning-list">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            ) : null}
          </Note>
        </div>
      ) : (
        <div className="le-card-body">
          {status?.status === 'partial' && warnings.length ? (
            <Disclosure summary={`Partial result — ${warnings.length} note${warnings.length === 1 ? '' : 's'}`}>
              <ul className="le-warning-list">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            </Disclosure>
          ) : null}
          {children}
        </div>
      )}
    </section>
  );
}

export function SubHeading({ children, right, caption }) {
  return (
    <div className="le-subheading">
      <div className="le-subheading-main">
        <h4>{children}</h4>
        {caption ? <span className="le-subheading-caption">{caption}</span> : null}
      </div>
      {right ? <div className="le-subheading-right">{right}</div> : null}
    </div>
  );
}

export function Segmented({ options, value, onChange, small = true, ariaLabel }) {
  return (
    <div className={`le-segmented ${small ? 'le-segmented-sm' : ''}`} role="tablist" aria-label={ariaLabel}>
      {options.map((opt) => (
        <button key={opt.value} type="button" role="tab" aria-selected={value === opt.value} className={value === opt.value ? 'active' : ''} onClick={() => onChange(opt.value)}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}
